import {
  collab,
  type CoreEditor,
  EditorState,
  getVersion,
  type JSONContent,
  Node,
  receiveTransaction,
  sendableSteps,
  type SendableWordCountChange,
  sendableWordCountChange,
  Step,
} from "@vericus/cadmus-editor-prosemirror";

import { captureException, captureMessage } from "@sentry/react";
import { Channel, Presence } from "phoenix";
import { debounce } from "ts-debounce";

import { globalHeaders } from "@/client/headers";
import { API_ENDPOINT } from "@/config";

import { isRetryableSaveError, SaveError } from "./authority-slice";
import { confirmedDoc } from "./confirmed-doc";
import { PresenceMeta } from "./local-collab-presence";
import {
  SendableChangeset,
  SendableChangesetStore,
} from "./sendable-changeset-store";
import { type StreamConnectionProps } from "./stream-connection";
import { withRequestTimeout } from "./with-request-timeout";

//// Constants
const SAVE_DEBOUNCE = 2000; // Debounce time in milliseconds for sending steps
const POLL_INTERVAL = 8000; // Polling interval in milliseconds
const SEND_BACKOFF_MAX_MS = 30_000; // Ceiling for the send backoff.
const REQUEST_TIMEOUT_MS = 30_000; // A stall becomes a retryable error, not a hang.
const STALE_RECOVERY_THRESHOLD_MS = 24 * 60 * 60 * 1000; // to flag a replay done after this age
const LIVE_BUFFER_CLIENT_ID = "live-buffer"; // Stands in for the live CollabState unconfirmed buffer's author.
const CONFIRMED_FOREIGN_CLIENT_ID = "confirmed-foreign"; // Stands in for a confirmed step that is not the changeset's

// Communication state of the StreamConnectionWithStore instance
enum CommState {
  INIT = "init",
  POLL = "poll",
  SEND = "send",
  FAIL = "fail",
}

/** Why a rebase declined for a sendable changeset's steps. */
type RebaseFailureReason =
  | "own_work_ahead"
  | "rebase_dropped"
  | "already_confirmed"
  | "backup_ahead_of_head"
  | "incomplete_server_steps"
  | "server_steps_unparseable"
  | "sendable_changeset_steps_unparseable";

/** Why a candidate did not end up applied, beyond the rebase itself. */
type RecoverFailureReason =
  | RebaseFailureReason
  | "doc_moved"
  | "inject_failed"
  | "history_read_failed";

/**
 * Outcome of a rebase attempt
 */
type RebaseOutcome =
  | {
      ok: true;
      steps: Record<string, unknown>[];
      /** The state these positions were computed against. */
      from: EditorState;
    }
  | { ok: false; reason: RebaseFailureReason };

/**
 * Outcome of a recover attempt
 */
type RecoverOutcome =
  | { ok: true; reason: "replayed" }
  | { ok: false; reason: RecoverFailureReason };

/**
 * Every reason a changeset can be dropped for, including changesets
 * never attempted.
 */
type DropReason = RecoverOutcome["reason"] | "not_attempted";

//////////////////////////////////////////////////////////////////////////////
// HTTP API                                                                 //
//////////////////////////////////////////////////////////////////////////////

// Payload for `stream.version` channel message indicating that a new Stream
// version has been confirmed.
interface StreamVersionMsg {
  // The updated stream
  stream_id: string;
  // The client that sent the update
  client_id: string;
  // The new confirmed version
  version: number;
}

// Payload for `client.selection` channel message indicating a Selection update
// in a Editor connected to the collaborative Stream.
interface EditorSelectionMsg {
  client_id: string;
  selection: {
    kind: "editor";
    stream_id: string;
    anchor: number;
    head: number;
  };
}

interface Transaction {
  version: number;
  steps: readonly Step[];
  client_id: string;
}

type AppendResult =
  | { status: "ok" }
  | { status: "conflict" }
  | { status: "error"; error: SaveError };

interface ReadStepsResult {
  steps: ServerStep[];
  version: number;
}

// Return of the readSteps API.
type ReadStepsOutcome = { ok: true; result: ReadStepsResult } | { ok: false };

interface ServerStep {
  /** Step Data */
  data: unknown;
  /** Client ID that sent the step */
  client_id: string;
}

interface Stream {
  stream_id: string;
  version: number;
  initial_state: JSONContent;
}

interface APIProps {
  /// Cadmus Tenant
  tenant: string;
  /// Authority Stream ID
  streamId: string;
  /// Optional Student Channel topic.
  studentTopic?: string;
}

class AuthorityAPI {
  tenant: string;
  streamId: string;
  studentTopic?: string;

  constructor(props: APIProps) {
    this.tenant = props.tenant;
    this.streamId = props.streamId;
    this.studentTopic = props.studentTopic;
  }

  /**
   * Ensure a Stream is setup for an Answer Block.
   *
   * @param answerBlockId - Used as the Stream ID.
   * @param initialState - The Stream at version 0.
   * @returns The Stream.
   *
   * @throws Error if not created; reload.
   */
  async setupAuthorityStream(initialState: JSONContent): Promise<Stream> {
    const headers = new Headers({
      ...globalHeaders(),
      "Content-Type": "application/json",
      Accept: "application/json",
    });

    const resp = await withRequestTimeout(
      "authoritySetupStream",
      REQUEST_TIMEOUT_MS,
      (signal) =>
        fetch(`${API_ENDPOINT}/api/authority/streams`, {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({
            stream_id: this.streamId,
            initial_state: initialState,
          }),
          signal,
        })
    );

    if (resp.ok) {
      const stream: Stream = await resp.json();
      return stream;
    }

    throw new Error("StreamCreationError");
  }

  async appendTransaction(transaction: Transaction): Promise<AppendResult> {
    const headers = new Headers({
      ...globalHeaders(),
      "Content-Type": "application/json",
    });

    const body = {
      transaction,
      student_topic: this.studentTopic,
    };

    let resp: Response;
    try {
      resp = await withRequestTimeout(
        "authorityAppend",
        REQUEST_TIMEOUT_MS,
        (signal) =>
          fetch(`${API_ENDPOINT}/api/authority/transactions/${this.streamId}`, {
            method: "POST",
            headers,
            credentials: "include",
            body: JSON.stringify(body),
            signal,
          })
      );
    } catch (e) {
      // Offline, reset or abort: retryable.
      return {
        status: "error",
        error: {
          kind: "network",
          title: "Network disruption",
          detail: e instanceof Error ? e.message : undefined,
        },
      };
    }

    if (resp.ok && resp.status === 204) {
      return { status: "ok" };
    }

    if (resp.status === 409) {
      return { status: "conflict" };
    }

    // 401 is the only terminal case. Everything else stays retryable,
    // away from the reload path.
    if (resp.status === 401) {
      return {
        status: "error",
        error: { kind: "unauthorized", title: "Expired session" },
      };
    }

    // Reached and refused, told apart from never reached so we don't
    // blame the connection. Retryable: 429 and 408 pass. 409 is a
    // rebase.
    if (resp.status >= 400 && resp.status < 500) {
      return {
        status: "error",
        error: {
          kind: "rejected",
          title: "Cannot save",
          detail: `Request rejected with status ${resp.status}`,
        },
      };
    }

    return {
      status: "error",
      error: {
        kind: "network",
        title: "Network disruption",
        detail: `Unexpected response status ${resp.status}`,
      },
    };
  }

  async readSteps(version: number): Promise<ReadStepsOutcome> {
    const headers = new Headers({
      ...globalHeaders(),
      Accept: "application/json",
    });

    let resp: Response;
    try {
      resp = await withRequestTimeout(
        "authorityReadSteps",
        REQUEST_TIMEOUT_MS,
        (signal) =>
          fetch(
            `${API_ENDPOINT}/api/authority/steps/${this.streamId}?version=${version}`,
            {
              method: "GET",
              headers,
              credentials: "include",
              signal,
            }
          )
      );
    } catch {
      return { ok: false };
    }

    if (resp.ok) {
      try {
        const body = (await resp.json()) as ReadStepsResult;
        return { ok: true, result: body };
      } catch {
        // Not JSON at all: an error page, a cut body.
        return { ok: false };
      }
    }

    return { ok: false };
  }
}

/** Props for a StreamConnectionWithStore. */
export interface StreamConnectionWithStoreProps extends StreamConnectionProps {
  store: SendableChangesetStore;
}

/**
 * Equality ignoring object key order. Arrays stay ordered.
 */
function deepEqualUnordered(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  )
    return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length)
      return false;
    return a.every((v, i) => deepEqualUnordered(v, b[i]));
  }

  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  if (keys.length !== Object.keys(right).length) return false;
  return keys.every((k) => k in right && deepEqualUnordered(left[k], right[k]));
}

/**
 * Connect a TipTap `Editor` to a collaborative Authority's `Stream`.
 *
 * ## Authority Stream
 *
 * A `Stream` is an append-only data structure storing ordered
 * operation-transform `Step` steps. Steps can only be appended to the stream if
 * the client provided version matches, otherwise the client must first rebase
 * it's local state and catch up before trying a subsequent write.
 *
 * The `Editor` instance has a confirmed internal `version` number and a buffer
 * of un-confirmed steps (provided by the `collab` extension and plugin).
 *
 * Connecting the `Editor` to a back-end Authority Stream will:
 *
 *  1. Load the initial state of the Editor by catching up to the latest steps.
 *
 *  2. Regularly listen to the Work Channel for new steps.
 *
 *  3. Send local steps to the Authority server to append to the stream and
 *     handle the re-basing on conflicts.
 *
 *  4. Generate snapshots for confirmed versions of the EditorState.
 *
 * ## Communication channels
 *
 * The `Editor` can be *connected* to Authority Stream via the REST API and a
 * WebSocket channel. The REST API is used for transactional and stateless
 * operations for writing and reading a Stream. The WebSocket channel is used
 * for eagerly listening to writes from other clients and syncing carets for
 * visual fidelity.
 *
 * NOTE: The absence of the websocket connection should not have a critical
 * impact as the transactional nature for the REST API will assist the Editor in
 * catching up on conflicts.
 *
 * The Cadmus Authority REST API exposes the following endpoints:
 *
 *   1. `AppendSteps` - Append steps on a Stream at the expected version.
 *   Returns 204 accepted or 409 conflict.
 *
 *   2. `ReadSteps` - Read steps from a version.
 *
 * The WebSocket channel uses an existing `Phoenix.Channel` connected to a
 * Pantheon Work topic.
 *
 * Channel listens to the messages:
 *
 *   1. `stream.version` with payload `StreamVersionMsg`.
 *
 * Channel pushes the messages:
 *
 *   1. `client.selection` with payload `EditorSelectionMsg`.
 *
 * ## Usage
 *
 * Initialise the connection instance:
 *
 *     const editor = createCadmusEditor({ ...props })
 *     const conn = new StreamConnectionWithStore({ editor, ...props });
 *
 * Asynchronously await the connection:
 *
 *     await conn.connect();
 *
 * The `connect()` is asynchronous and **must** be awaited to ensure the
 * `Editor` is ready for interaction.
 */
export class StreamConnectionWithStore {
  editor: CoreEditor;
  tenant: string;
  streamId: string;
  clientId: string;
  httpClient: AuthorityAPI;

  private studentTopic?: string;
  private channel?: Channel;
  private presence?: Presence;

  private store: SendableChangesetStore;

  private onSnapshot?: (snapshot: {
    doc: JSONContent;
    version: number;
    wordCountChange: SendableWordCountChange;
  }) => void;
  private onFailure?: () => void;
  private onSendError?: (error: SaveError) => void;
  private onSendRecovered?: () => void;

  private initialState: JSONContent;
  private comm: CommState = CommState.INIT;

  /** True until recover() has listed this stream's backups. */
  private recoveryPending = true;

  /** Failed sends, and whether one waits. */
  private failedSends = 0;
  private backingOff = false;
  private backoffTimerRef: NodeJS.Timeout | undefined = undefined;

  private channelRefs: Array<[string, number]> = [];
  private pollTimerRef: NodeJS.Timeout | undefined = undefined;

  constructor(props: StreamConnectionWithStoreProps) {
    this.tenant = props.tenant;
    this.editor = props.editor;
    this.streamId = props.streamId;
    this.clientId = props.clientId;
    this.onSnapshot = props.onSnapshot;
    this.onFailure = props.onFailure;
    this.onSendError = props.onSendError;
    this.onSendRecovered = props.onSendRecovered;
    this.initialState = props.initialState;

    // Select a channel based on the context.
    this.studentTopic = props.studentChannel?.topic;
    this.channel = props.studentChannel?.channel;
    this.presence = props.studentChannel?.presence;

    // offline SendableChangeset store
    this.store = props.store;

    this.editor.on("destroy", () => {
      this.clean();
      this.store.close();
    });

    this.httpClient = new AuthorityAPI({
      tenant: props.tenant,
      streamId: props.streamId,
      studentTopic: this.studentTopic,
    });
  }

  /**
   * Setup the connection and start collaboration.
   *
   * Ensures the Stream is ready and the Editor is caught up with Stream's
   * head before interaction on Editor begins.
   */
  connect = async () => {
    await this.httpClient.setupAuthorityStream(this.initialState);

    // Save local changes
    const debouncedSendSteps = debounce(this.sendSteps, SAVE_DEBOUNCE, {
      maxWait: SAVE_DEBOUNCE * 4,
    });
    this.editor.on("update", debouncedSendSteps);

    // Carets sync on focus
    this.editor.on("selectionUpdate", ({ transaction }) => {
      if (!transaction.docChanged) {
        this.sendCaret();
      }
    });

    // Flush on reconnect, not on the next keystroke.
    window.addEventListener("online", this.handleOnline);

    const editorCaughtUp = await this.poll();

    if (this.comm === CommState.INIT) this.comm = CommState.POLL;

    // Listen to changes
    this.listen();

    if (editorCaughtUp) {
      void this.recover().catch((error) => {
        captureException(error, {
          level: "warning",
          tags: { offline_recovery: "recover_threw" },
          extra: { streamId: this.streamId },
        });
      });
    } else {
      this.recoveryPending = false;
    }
  };

  // Retry in-memory buffered steps on reconnect
  private handleOnline = () => {
    if (this.comm === CommState.FAIL) return;
    void this.sendSteps().catch((error) => {
      captureException(error, {
        level: "warning",
        tags: { offline_recovery: "reconnect_flush_failed" },
        extra: { streamId: this.streamId },
      });
    });
  };

  // tear down listeners and timer refs
  private clean = () => {
    window.removeEventListener("online", this.handleOnline);
    if (this.channelRefs.length > 0 && this.channel) {
      for (const [event, ref] of this.channelRefs) {
        this.channel.off(event, ref);
      }
    }
    if (this.pollTimerRef) {
      clearInterval(this.pollTimerRef);
    }
    clearTimeout(this.backoffTimerRef);
  };

  // Listen for changes on the WorkChannel if available, or regular polling.
  private listen = async () => {
    if (this.channel) {
      const versionRef = this.channel.on(
        "stream.version",
        (payload: StreamVersionMsg) => {
          if (
            payload.stream_id === this.streamId &&
            payload.version > getVersion(this.editor.state) &&
            payload.client_id !== this.clientId
          ) {
            this.poll();
          }
        }
      );
      this.channelRefs = [["stream.version", versionRef]];
    } else {
      this.pollTimerRef = setInterval(async () => {
        if (this.comm === CommState.POLL) {
          await this.poll();
        }
      }, POLL_INTERVAL);
    }
  };

  // poll for the latest confirmed Step from Stream since this client's
  // current confirmed Version and apply to the current editor doc to sync with Stream
  private poll = async (): Promise<boolean> => {
    // no-op if comm state is currently FAIL
    if (this.comm === CommState.FAIL) return false;

    const outcome = await this.httpClient.readSteps(
      getVersion(this.editor.state)
    );
    // A failed read is not a verified "nothing new"; do not report a
    // caught-up head that was never actually confirmed.
    if (!outcome.ok) return false;
    const { steps, version } = outcome.result;

    // no-op if there is a send in flight
    if (this.comm === CommState.SEND) return false;

    const applied = this.receiveSteps(
      steps.map((s) => Step.fromJSON(this.editor.schema, s.data)),
      steps.map((s) => s.client_id),
      version
    );

    this.drawCarets();

    return applied && (this.comm as CommState) !== CommState.FAIL;
  };

  // Apply Steps to the live editor state
  private receiveSteps = (
    steps: readonly Step[],
    clientIds: string[],
    version: number,
    onNewState?: (state: EditorState) => void
  ): boolean => {
    // no-op here - nothing to apply
    if (steps.length === 0) return true;

    // stale if a concurrent receiveSteps already advanced the version past
    // what these steps were computed for originally
    if (getVersion(this.editor.state) !== version) {
      captureMessage("StreamConnection received steps for a stale version", {
        level: "warning",
        tags: { offline_recovery: "stale_version" },
        extra: { streamId: this.streamId, version },
      });
      return false;
    }

    try {
      this.editor.commands.receiveSteps(steps, clientIds, onNewState);
      this.syncSendableChangeset(this.editor.state);
    } catch (e) {
      this.comm = CommState.FAIL;
      this.onFailure?.();
      this.clean();

      captureException(e, {
        level: "error",
        tags: { offline_recovery: "apply_failure" },
        extra: { streamId: this.streamId, version },
      });
      return false;
    }

    return true;
  };

  // Send the Editor's unconfirmed Steps to the Stream.
  private sendSteps = async () => {
    // Already sending: let that request finish rather than racing a
    // second one against the same base version.
    if (this.comm === CommState.SEND) return;

    const state = this.editor.state;
    const sendable = sendableSteps(this.editor.state);

    // write the sendable to the offline store
    if (sendable) {
      await this.syncSendableChangeset(state);
    }

    // FAIL state nothing to do
    if (this.comm === CommState.FAIL) return;
    // Wait out the backoff, not the next keystroke.
    if (this.backingOff) return;

    if (sendable) {
      this.comm = CommState.SEND;

      // capturing the pre-await state doc because the live doc may carry steps
      // this transaction does not send, and snapshotting it tags a newer doc
      // with this version.
      const docAtSend = state.doc;

      const transaction = {
        version: sendable.version,
        steps: sendable.steps,
        client_id: sendable.clientID.toString(),
      };

      const result = await this.httpClient.appendTransaction(transaction);
      if (result.status === "ok") {
        const sendableChange = sendableWordCountChange(sendable);
        const handled = this.receiveSteps(
          transaction.steps,
          transaction.steps.map(() => transaction.client_id),
          transaction.version,
          () => {
            this.onSnapshot?.({
              doc: docAtSend.toJSON(),
              version: transaction.version + transaction.steps.length,
              wordCountChange: sendableChange,
            });
          }
        );
        this.comm = CommState.POLL;

        this.clearSendBackoff();
        this.onSendRecovered?.();

        if (!handled) {
          this.sendSteps();
        } else {
          // Caret after steps have been accepted
          this.sendCaret();
        }
      }
      if (result.status === "conflict") {
        this.clearSendBackoff();
        this.comm = CommState.POLL;
        await this.poll();
      }
      if (result.status === "error") {
        this.comm = isRetryableSaveError(result.error)
          ? CommState.POLL
          : CommState.FAIL;

        this.failedSends += 1;
        const wait = Math.min(
          SAVE_DEBOUNCE * 2 ** (this.failedSends - 1),
          SEND_BACKOFF_MAX_MS
        );
        this.backingOff = true;

        clearTimeout(this.backoffTimerRef);
        this.backoffTimerRef = setTimeout(() => {
          this.backingOff = false;
          void this.sendSteps().catch((error) => {
            captureException(error, {
              level: "warning",
              tags: { offline_recovery: "backoff_retry_failed" },
              extra: { streamId: this.streamId },
            });
          });
        }, wait);

        this.onSendError?.(result.error);
      }
    }
  };

  private clearSendBackoff = () => {
    this.failedSends = 0;
    this.backingOff = false;
    clearTimeout(this.backoffTimerRef);
    this.backoffTimerRef = undefined;
  };

  // Publish the Selection to the StudentChannel as `client.selection` message.
  private sendCaret = () => {
    // FAIL state nothing to do
    if (this.comm === CommState.FAIL) {
      return;
    }
    if (this.channel) {
      this.channel.push("client.selection", {
        client_id: this.clientId,
        selection: {
          kind: "editor",
          stream_id: this.streamId,
          anchor: this.editor.state.selection.anchor,
          head: this.editor.state.selection.head,
        },
      } satisfies EditorSelectionMsg);
    }
  };

  // Draw carets for connected clients with a to this Editor.
  private drawCarets = () => {
    if (this.presence) {
      const carets: { clientId: string; head: number }[] = [];
      this.presence.list((_userId, { metas }: { metas: PresenceMeta[] }) => {
        for (const meta of metas) {
          if (
            meta.client &&
            meta.client.selection &&
            meta.client.selection.kind === "editor" &&
            meta.client.selection.stream_id === this.streamId
          ) {
            carets.push({
              clientId: meta.client.client_id,
              head: meta.client.selection.head,
            });
          }
        }
      });
      if (carets.length > 0) {
        this.editor.commands.updateCarets(carets);
      }
    }
  };

  /**
   * Synchronise the unconfirmed buffer as a SendableChangeset in the offline
   * store.
   *
   * The offline store will either be left with an entry with unfonfirmed steps
   * or cleared if all steps are confirmed.
   */
  private syncSendableChangeset = async (
    state: EditorState
  ): Promise<undefined> => {
    try {
      const doc = confirmedDoc(this.editor, state);
      const sendable = sendableSteps(state);
      const sendableChangeset = {
        streamId: this.streamId,
        clientId: this.clientId,
        version: getVersion(state),
        doc: doc.toJSON(),
        steps: sendable ? sendable.steps.map((s) => s.toJSON()) : [],
        savedAt: Date.now(),
      };

      // delete changeset in store if no steps in the unconfirmed buffer
      if (sendableChangeset.steps.length === 0) {
        if (this.recoveryPending) return;
        await this.store.delete(
          sendableChangeset.streamId,
          sendableChangeset.clientId
        );
        return;
      }
      await this.store.put(sendableChangeset);
    } catch (e) {
      captureException(e, {
        level: "warning",
        tags: { offline_backup: "save_failed" },
        extra: { streamId: this.streamId },
      });
    }
  };

  /**
   * Replay work an earlier session left behind for this stream.
   *
   * Only the newest candidate is tried. Whether or not it replays, every
   * other candidate is dropped unattempted.
   */
  private recover = async () => {
    const sendableChangesets = await this.store.list(this.streamId);
    this.recoveryPending = false;

    // filter candidates satisfying the heurestic
    // no empty steps and sorted from most recent first
    const [newest, ...rest] = sendableChangesets
      .filter((b) => b.steps.length > 0)
      .sort((a, b) => b.savedAt - a.savedAt);
    if (!newest) return;

    const attempt = await this.recoverAttempt(newest, rest.length + 1);
    const toDrop: Array<{
      sendableChangeset: SendableChangeset;
      reason: DropReason;
    }> = [
      { sendableChangeset: newest, reason: attempt.reason },
      ...rest.map((sendableChangeset) => ({
        sendableChangeset,
        reason: "not_attempted" as const,
      })),
    ];

    for (const { sendableChangeset, reason } of toDrop) {
      // Don't drop if sendableChangeset is of the same ID because
      // it may be holding the current in-memory unconfirmed buffer
      // this will be dropped by the confirmation loop later
      if (reason === "replayed" && sendableChangeset.clientId === this.clientId)
        continue;
      await this.dropBackup(sendableChangeset, reason);
    }
  };

  /**
   * Attempt a Rebase for one candidate's steps onto the live doc, or
   * say why it couldnt.
   */
  private recoverAttempt = async (
    sendableChangeset: SendableChangeset,
    candidateCount: number
  ): Promise<RecoverOutcome> => {
    const stepsSinceSendableChangeset = await this.httpClient.readSteps(
      sendableChangeset.version
    );

    if (!stepsSinceSendableChangeset.ok) {
      captureMessage("StreamConnection could not recover a changeset", {
        level: "warning",
        tags: { offline_recovery: "history_read_failed" },
        extra: {
          streamId: sendableChangeset.streamId,
          clientId: sendableChangeset.clientId,
        },
      });
      return { ok: false, reason: "history_read_failed" };
    }

    const { steps: serverSteps } = stepsSinceSendableChangeset.result;
    const outcome = this.rebaseAttempt(sendableChangeset, serverSteps);

    if (!outcome.ok) {
      captureMessage("StreamConnection could not recover a changeset", {
        level: "warning",
        tags: { offline_recovery: outcome.reason },
        extra: {
          streamId: sendableChangeset.streamId,
          clientId: sendableChangeset.clientId,
        },
      });
      return { ok: false, reason: outcome.reason };
    }

    if (outcome.from !== this.editor.state) {
      captureMessage("StreamConnection could not recover a changeset", {
        level: "warning",
        tags: { offline_recovery: "doc_moved" },
        extra: {
          streamId: sendableChangeset.streamId,
          clientId: sendableChangeset.clientId,
        },
      });
      return { ok: false, reason: "doc_moved" };
    }

    if (!this.injectLocalSteps(outcome.steps)) {
      captureMessage("StreamConnection could not recover a changeset", {
        level: "warning",
        tags: { offline_recovery: "inject_failed" },
        extra: {
          streamId: sendableChangeset.streamId,
          clientId: sendableChangeset.clientId,
        },
      });
      return { ok: false, reason: "inject_failed" };
    }

    const age = Date.now() - sendableChangeset.savedAt;
    captureMessage("StreamConnection replayed a changeset", {
      level: age > STALE_RECOVERY_THRESHOLD_MS ? "warning" : "info",
      tags: {
        offline_recovery:
          age > STALE_RECOVERY_THRESHOLD_MS ? "replayed_stale" : "replayed",
      },
      extra: {
        streamId: sendableChangeset.streamId,
        recoveredClientId: sendableChangeset.clientId,
        thisClientId: this.clientId,
        ageMs: age,
        // How many candidates there were; all but this one are dropped
        // unattempted right after.
        candidateCount,
        droppedUnattempted: candidateCount - 1,
      },
    });

    void this.sendSteps();
    return { ok: true, reason: "replayed" };
  };

  // Drop a backup, reporting whether it is gone.
  private dropBackup = async (
    sendableChangeset: SendableChangeset,
    reason: DropReason
  ): Promise<boolean> => {
    try {
      await this.store.delete(
        sendableChangeset.streamId,
        sendableChangeset.clientId
      );
      return true;
    } catch (error) {
      captureException(error, {
        level: "warning",
        tags: { offline_recovery: "clear_failed" },
        extra: { streamId: sendableChangeset.streamId, reason },
      });
      return false;
    }
  };

  /**
   * Attempt a rebase a SendableChangeset's steps onto the live doc.
   */
  private rebaseAttempt = (
    sendableChangeset: SendableChangeset,
    serverSteps: ServerStep[]
  ): RebaseOutcome => {
    const schema = this.editor.schema;
    const liveEditor = this.editor.state;
    const vLive = getVersion(liveEditor);

    if (sendableChangeset.version > vLive)
      return { ok: false, reason: "backup_ahead_of_head" };

    const ahead = vLive - sendableChangeset.version;
    if (serverSteps.length < ahead)
      return { ok: false, reason: "incomplete_server_steps" };

    // Rebuild the sendableChangeset at its own version and replay its steps
    let state: EditorState;
    try {
      const base = EditorState.create({
        doc: Node.fromJSON(schema, sendableChangeset.doc),
        plugins: [
          collab({
            version: sendableChangeset.version,
            clientID: sendableChangeset.clientId,
          }),
        ],
      });
      const replay = base.tr;
      for (const s of sendableChangeset.steps) {
        replay.step(Step.fromJSON(schema, s));
      }
      state = base.apply(replay);
    } catch {
      return { ok: false, reason: "sendable_changeset_steps_unparseable" };
    }

    // Rebase past all confirmed since (→ P′).
    // read about rebase mechanics more in-depth here:
    // https://claude.ai/code/artifact/355c055c-be5f-4672-a248-e9d6fa42d7e8
    const confirmed = serverSteps.slice(0, ahead);

    // Best-effort: how much of the read is this backup's own accepted work
    let ownPrefix = 0;
    while (
      ownPrefix < serverSteps.length &&
      ownPrefix < sendableChangeset.steps.length &&
      this.sameStep(
        serverSteps[ownPrefix]?.data,
        sendableChangeset.steps[ownPrefix]
      )
    ) {
      ownPrefix += 1;
    }

    // The sendableChangeset's own accepted work reaches past what this
    // editor applied
    if (ownPrefix > ahead) return { ok: false, reason: "own_work_ahead" };

    // Own guard, like the sendableChangeset above: a step or mark
    // type this build lacks throws here.
    let confirmedSteps: Step[];
    try {
      confirmedSteps = confirmed.map((s) => Step.fromJSON(schema, s.data));
    } catch {
      return { ok: false, reason: "server_steps_unparseable" };
    }

    state = state.apply(
      receiveTransaction(
        state,
        confirmedSteps,
        confirmed.map((_, i) =>
          i < ownPrefix
            ? sendableChangeset.clientId
            : CONFIRMED_FOREIGN_CLIENT_ID
        ),
        {}
      )
    );

    // Then past our own steps, which the confirmed serverSteps knows
    // nothing about.
    const unconfirmed = sendableSteps(liveEditor);
    if (unconfirmed) {
      state = state.apply(
        receiveTransaction(
          state,
          unconfirmed.steps,
          unconfirmed.steps.map(() => LIVE_BUFFER_CLIENT_ID),
          {}
        )
      );
    }

    const rebasedSendableSteps = sendableSteps(state);

    if (!rebasedSendableSteps || rebasedSendableSteps.steps.length === 0)
      return {
        ok: false,
        reason:
          ownPrefix === sendableChangeset.steps.length
            ? "already_confirmed"
            : "rebase_dropped",
      };

    return {
      ok: true,
      steps: rebasedSendableSteps.steps.map((s) => s.toJSON()),
      from: liveEditor,
    };
  };

  /**
   * Whether two serialised steps are the same step.
   *
   * A best-effort content match.
   * Anything unparseable is not a match.
   */
  private sameStep = (a: unknown, b: unknown): boolean => {
    try {
      const schema = this.editor.schema;
      return deepEqualUnordered(
        Step.fromJSON(schema, a).toJSON(),
        Step.fromJSON(schema, b).toJSON()
      );
    } catch {
      return false;
    }
  };

  /**
   * Dispatch the rebased steps into the live editor.
   *
   * Reports whether they landed. A dispatch that
   * applies nothing must never read as a replay: the
   * caller deletes the sendableChangeset on a true return, so a
   * false positive drops the only copy.
   */
  private injectLocalSteps = (
    injectableSteps: Record<string, unknown>[]
  ): boolean => {
    try {
      const before = this.editor.state;
      const tr = before.tr;
      for (const s of injectableSteps)
        tr.step(Step.fromJSON(this.editor.schema, s));

      // Recovered work is not something the student
      // just did, so it must not be what their next
      // undo undoes.
      tr.setMeta("addToHistory", false);

      this.editor.view.dispatch(tr);
      return true;
    } catch (error) {
      captureException(error, {
        level: "warning",
        tags: { offline_recovery: "inject_failed" },
        extra: { streamId: this.streamId },
      });
      return false;
    }
  };
}
